import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store";
import { signalsListSelector, selectSignal, updateViewedSignals, getSignals } from "@store/signals";
import { useMixpanel } from "@taskpane/context";
import { BrandIcon, RefreshIcon } from "@taskpane/icons";
import { useContentHeight } from "@taskpane/hooks";
import { withTransition } from "@taskpane/hoc";
import { ErrorOverlay, Header, HeaderOption } from "@taskpane/components";
import { FilterIcon } from "./icons";
import { Filters, SignalDetails, SignalList } from "./components";
import css from "./SignalsScreen.module.scss";

const SignalsScreen: React.FC = () => {
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.auth.user);
  const { list, headerItemIndices } = useAppSelector(signalsListSelector);
  const selectedSignal = useAppSelector((state) => state.signals.selectedSignal);
  const isLoading = useAppSelector((state) => state.signals.isLoading.signals);
  const isError = useAppSelector((state) => state.signals.isError.signals);
  const nextPage = useAppSelector((state) => state.signals.pagination.nextPage);
  const unviewedSignalsCount = useAppSelector(
    (state) => state.signals.pagination.notViewedSignalsCount
  );

  const { trackEvent } = useMixpanel();

  const isEmpty = !isLoading && !isError && !list.length;

  const [activeHeaderOption, setActiveHeaderOption] = useState<string | null>(null);

  const headerWrapperRef = useRef<HTMLDivElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  const headerOptions = useMemo<HeaderOption[]>(
    () => [
      {
        id: "filter",
        icon: FilterIcon,
        render: () => <Filters />,
      },
    ],
    []
  );

  const attrs = {
    container: {
      className: css.container,
    },
    headerWrapper: {
      ref: headerWrapperRef,
      className: css.headerWrapper,
    },
    header: {
      className: css.header,
      isLoading: !list.length && isLoading,
      title: "Signals",
      options: headerOptions,
      onActiveOptionChange: setActiveHeaderOption,
    },
    contentWrapper: {
      ref: contentWrapperRef,
      className: css.contentWrapper,
    },
    emptyState: {
      className: css.emptyState,
    },
    icon: {
      className: css.icon,
    },
    signalList: {
      isLoading,
      list,
      headerItemIndices,
      hasNextPage: !!nextPage,
    },
    signalDetails: {
      signal: selectedSignal,
      onClose: () => {
        trackEvent("signalsScreen.closeSignalClick");
        dispatch(selectSignal(null));
      },
    },
    errorOverlay: {
      isOpened: isError,
      buttonIcon: RefreshIcon,
      description: "Please refresh to try again",
      title: "Signals error",
      onClick: () => {
        dispatch(getSignals());
      },
    },
  };

  useEffect(() => {
    trackEvent("signalsScreen.view");
  }, [trackEvent]);

  useContentHeight(contentWrapperRef, [headerWrapperRef], {
    offset: 73,
    useWindowHeight: true,
    deps: [activeHeaderOption],
  });

  useEffect(() => {
    if (user && unviewedSignalsCount) {
      dispatch(updateViewedSignals());
    }
  }, [dispatch, user, unviewedSignalsCount]);

  return (
    <div {...attrs.container}>
      <div {...attrs.headerWrapper}>
        <Header {...attrs.header} />
      </div>
      <div {...attrs.contentWrapper}>
        {isEmpty ? (
          <div {...attrs.emptyState}>
            <BrandIcon {...attrs.icon} />
            <span>You don’t have any signals yet</span>
          </div>
        ) : (
          <SignalList {...attrs.signalList} />
        )}
        <ErrorOverlay {...attrs.errorOverlay} />
      </div>
      <SignalDetails {...attrs.signalDetails} />
    </div>
  );
};

export default withTransition(SignalsScreen);
